import { useCallback, useState } from 'react'
import { mapModelToModelRecord } from 'models/model/model.utils'
import { Button, Modal } from 'src/components/ui'
import {
  AddInputTagsContent,
  ControllableInputTagsSelector,
} from 'models/create'
import { FetchPrescriptiveModel, ModelInputTag } from 'src/services'
import { useTags } from 'tags/api'
import { isDefined } from 'src/types'

type Props = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  model: FetchPrescriptiveModel
  uncontrollableTags: ModelInputTag[]
  controllableTags: ModelInputTag[]
  onConfirm: (controllableTags: string[], inputTags: string[]) => void
  isMutating?: boolean
}
enum ModalStep {
  EDIT_CONTROLLABLE_TAGS = 'EDIT_CONTROLLABLE_TAGS',
  ADD_INPUT_TAGS = 'ADD_INPUT_TAGS',
}

export function EditInputTagsModal({
  model,
  isOpen,
  setIsOpen,
  controllableTags: initialControllableTags,
  uncontrollableTags: initialUncontrollableTags,
  onConfirm,
  isMutating = false,
}: Props): JSX.Element {
  const [step, setStep] = useState<ModalStep>(ModalStep.EDIT_CONTROLLABLE_TAGS)
  const { data: tags = [], isLoading } = useTags()

  const [controllableTags, setControllableTags] = useState<string[]>(
    initialControllableTags.map(tag => tag.node.tagName),
  )
  const [inputTags, setInputTags] = useState<string[]>([
    ...initialControllableTags.map(tag => tag.node.tagName),
    ...initialUncontrollableTags.map(tag => tag.node.tagName),
  ])

  const handleControllableChange = useCallback(
    (tag: string, controllable: boolean) => {
      setControllableTags(prev =>
        controllable ? [...prev, tag] : prev.filter(t => t !== tag),
      )
    },
    [],
  )
  const handleAddInputTag = useCallback((tag: string) => {
    setInputTags(prev => [...prev, tag])
    setStep(ModalStep.EDIT_CONTROLLABLE_TAGS)
  }, [])

  const handleRemoveInputTag = useCallback((tag: string) => {
    setInputTags(prev => prev.filter(t => t !== tag))
    setControllableTags(prev => prev.filter(t => t !== tag))
    setStep(ModalStep.EDIT_CONTROLLABLE_TAGS)
  }, [])

  const handleConfirmChanges = useCallback(() => {
    const controllableTagIds = controllableTags
      .map(tag => {
        const tagObj = tags.find(t => t.tagName === tag)
        return tagObj ? tagObj.tagNodeId : undefined
      })
      .filter(isDefined)

    const inputTagIds = inputTags
      .map(tag => {
        const tagObj = tags.find(t => t.tagName === tag)
        return tagObj ? tagObj.tagNodeId : undefined
      })
      .filter(isDefined)
    onConfirm(controllableTagIds, inputTagIds)
  }, [controllableTags, inputTags, onConfirm, tags])

  return (
    <Modal
      contentClassName="w-[1200px] !max-w-[90vw] !max-h-[1200px] h-[90vh]"
      isOpen={isOpen}
      close={() => setIsOpen(false)}
      title="Edit Input Tags"
    >
      <>
        <div className="flex size-full max-h-[calc(100%-48px)] py-s">
          {step === ModalStep.EDIT_CONTROLLABLE_TAGS && (
            <div className="flex w-full flex-col">
              <ControllableInputTagsSelector
                onAddInputTag={() => setStep(ModalStep.ADD_INPUT_TAGS)}
                inputTags={inputTags}
                controllableTags={controllableTags}
                setControllableTag={handleControllableChange}
                removeInputTag={handleRemoveInputTag}
              />
            </div>
          )}
          {step === ModalStep.ADD_INPUT_TAGS && (
            <div className="flex size-full">
              <AddInputTagsContent
                inputTags={inputTags}
                addInputTag={handleAddInputTag}
                removeInputTag={handleRemoveInputTag}
                model={mapModelToModelRecord(model)}
                isModal
                tags={tags}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-xs">
          <Button
            variant="primary"
            title="Save"
            disabled={isMutating}
            onClick={handleConfirmChanges}
          />
        </div>
      </>
    </Modal>
  )
}
